import { useEffect, useState } from "react";
import api from "@/lib/api";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { ADMIN_NAV } from "@/pages/admin/AdminHome";
import { Card } from "@/components/ui/card";
import { FinePrint } from "@/components/FinePrint";
import { riasecMeta } from "@/lib/riasec";
import { Compass, Users, ClipboardCheck, Loader2, AlertTriangle } from "lucide-react";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <Card className="p-5 card-elevate">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6"><Icon className="h-5 w-5 text-[#003057]" /></div>
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400">{label}</p>
          <p className="font-mono-num text-2xl font-bold text-[#003057]">{value}</p>
          {sub && <p className="text-xs text-slate-400">{sub}</p>}
        </div>
      </div>
    </Card>
  );
}

export function AdminAssessments() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/assessments/admin/summary").then(({ data }) => setD(data)).catch(() => setD({ error: true })); }, []);

  const areaCounts = d?.area_counts || [];
  const totalTop = areaCounts.reduce((s, a) => s + a.count, 0);
  const maxCount = Math.max(1, ...areaCounts.map((a) => a.count));

  return (
    <DashboardShell title="Career Interest Assessments" subtitle="O*NET Interest Profiler — cohort participation & interest areas" nav={ADMIN_NAV}>
      {!d && <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#003057]" /></div>}
      {d && (
        <div className="space-y-6">
          {d.available === false && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="admin-assessments-unavailable">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>The O*NET service is temporarily unavailable, so new assessments can't be scored right now. Existing totals are shown below.</span>
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Stat icon={Users} label="Students assessed" value={d.unique_students ?? 0} sub="unique participants" />
            <Stat icon={ClipboardCheck} label="Assessments taken" value={d.total_attempts ?? 0} sub="completed attempts" />
            <Stat icon={Compass} label="Interest areas" value={areaCounts.filter((a) => a.count > 0).length} sub="represented as top interest" />
          </div>

          <Card className="p-6 card-elevate">
            <h3 className="mb-1 font-heading text-lg font-bold text-[#003057]">Top interest area distribution</h3>
            <p className="mb-4 text-sm text-slate-500">How many students had each RIASEC area as their #1 interest.</p>
            {totalTop === 0 ? (
              <p className="rounded-lg bg-slate-50 px-4 py-6 text-center text-sm text-slate-400" data-testid="no-assessment-data">
                No completed assessments yet. Once students take the O*NET Interest Profiler, their top interest areas appear here.
              </p>
            ) : (
              <div className="space-y-3" data-testid="riasec-distribution">
                {areaCounts.map((a) => {
                  const m = riasecMeta(a.area);
                  return (
                    <div key={a.area}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="font-semibold text-[#003057]">{a.area} <span className="text-xs font-normal text-slate-400">· {m.tag}</span></span>
                        <span className="font-mono-num font-bold text-slate-600">{a.count}</span>
                      </div>
                      <div className="mt-1 h-3 w-full overflow-hidden rounded-full bg-slate-100">
                        <div className="h-full rounded-full transition-all" style={{ width: `${(a.count / maxCount) * 100}%`, backgroundColor: m.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <FinePrint>
            Aggregates count completed O*NET Interest Profiler attempts and the most common top interest area across
            students. No individual results or personal information are shown here — interest results are private to
            each student and shared read-only only at the student's choice. Scores are provided by the O*NET Web Services (USDOL/ETA).
          </FinePrint>
        </div>
      )}
    </DashboardShell>
  );
}
